import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading, StatusBadge, PriorityDot } from "@/components/common";
import { toast } from "sonner";
import {
  Search, Bot, User, UserCog, Send, ArrowLeft, ShieldCheck, Sparkles,
  AlertCircle, ChevronRight, Phone,
} from "lucide-react";

const safeLocale = (l) => { try { new Intl.Locale(l); return l; } catch { return "it"; } };
const dayKey = (iso) => { const d = new Date(iso); return isNaN(d) ? "unknown" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const fmtTime = (iso, lng) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleTimeString(safeLocale(lng), { hour: "2-digit", minute: "2-digit" }); };
export function groupByDay(convs, lng, t) {
  const today = dayKey(new Date().toISOString());
  const y = new Date(); y.setDate(y.getDate() - 1);
  const yesterday = dayKey(y.toISOString());
  const groups = new Map();
  [...convs].sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)).forEach((c) => {
    const k = dayKey(c.last_message_at || c.created_at);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(c);
  });
  return [...groups.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([k, items]) => ({
    key: k, items,
    label: k === today ? t("inbox.today") : k === yesterday ? t("inbox.yesterday") : k === "unknown" ? "—"
      : new Date(items[0].last_message_at || items[0].created_at).toLocaleDateString(safeLocale(lng), { day: "2-digit", month: "2-digit", year: "numeric" }),
  }));
}

function ConvList({ convs, activeId, onSelect, q, setQ }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex flex-col h-full border-r border-slate-200 bg-white">
      <div className="p-3 border-b border-slate-200">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="inbox-search" placeholder={t("inbox.search_ph2")} value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {convs.length === 0 && <div className="p-6 text-sm text-slate-400 text-center">{t("inbox.empty")}</div>}
        {groupByDay(convs, i18n.language, t).map((g) => (
          <div key={g.key} data-testid={`inbox-day-group-${g.key}`}>
            <div className="sticky top-0 z-[1] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50 border-y border-slate-100" data-testid="inbox-day-header">{g.label}</div>
            {g.items.map((c) => (
          <button key={c.id} data-testid="conv-item" onClick={() => onSelect(c)}
            className={`w-full flex items-start gap-3 p-3 text-left border-b border-slate-50 hover:bg-slate-50 ${activeId === c.id ? "bg-sky-50" : ""}`}>
            <div className="w-10 h-10 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-sm font-bold shrink-0">
              {c.customer?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#0F172A] truncate">{c.customer?.name}</span>
                {c.requires_operator && <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                <span className="ml-auto text-[10px] text-slate-400 shrink-0" data-testid="conv-time">{fmtTime(c.last_message_at, i18n.language)}</span>
              </div>
              <div className="text-[11px] text-slate-400 truncate">{c.customer?.phone}</div>
              <div className="text-xs text-slate-500 truncate">{c.last_message_preview || "—"}</div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <PriorityDot priority={c.priority} />
                <span className="text-[10px] uppercase tracking-wide text-slate-400">{c.mode === "human" ? t("inbox.operator") : t("inbox.ai_label")}</span>
                {c.project_status && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid="conv-project-status">{c.project_status}</span>}
              </div>
            </div>
            {c.unread > 0 && <span className="bg-sky-600 text-white text-[10px] rounded-full px-1.5 py-0.5">{c.unread}</span>}
          </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatThread({ conv, reload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  useEffect(() => { endRef.current?.scrollIntoView(); }, [conv?.messages?.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await http.post(`/conversations/${conv.id}/send`, { text }); setText(""); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const takeControl = async () => {
    try { await http.post(`/conversations/${conv.id}/take-control`); toast.success(t("inbox.control_taken")); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const releaseControl = async () => {
    try { await http.post(`/conversations/${conv.id}/release-control`); toast.success(t("inbox.control_released")); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const bubbleCls = (s) => s === "ai" ? "chat-bubble-ai ml-auto" : s === "operator" ? "chat-bubble-op ml-auto" : "chat-bubble-in mr-auto";

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="p-3 border-b border-slate-200 bg-white flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#0F172A] truncate">{conv.customer?.name}</div>
          <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Phone className="w-3 h-3" />{conv.customer?.phone}</div>
        </div>
        {conv.mode === "human" ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1"><UserCog className="w-3.5 h-3.5" /> {t("inbox.operator")}</span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"><Bot className="w-3.5 h-3.5" /> {t("inbox.ai_active")}</span>
        )}
        {conv.mode === "human"
          ? <Button data-testid="reactivate-ai-btn" size="sm" variant="outline" onClick={releaseControl} className="gap-1"><Sparkles className="w-3.5 h-3.5" /> {t("inbox.reactivate_ai")}</Button>
          : <Button data-testid="take-control-btn" size="sm" onClick={takeControl} className="bg-[#0F172A] hover:bg-[#1E293B] gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t("inbox.take_control")}</Button>}
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-2">
        {(conv.messages || []).map((m) => (
          <div key={m.id} data-testid="chat-message" className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${bubbleCls(m.sender)}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wide mb-0.5 flex items-center gap-1 text-slate-500">
              {m.sender === "customer" ? <User className="w-3 h-3" /> : m.sender === "ai" ? <Bot className="w-3 h-3" /> : <UserCog className="w-3 h-3" />}
              {m.sender === "customer" ? t("inbox.sender_customer") : m.sender === "ai" ? "AI" : (m.operator_name || t("inbox.operator"))}
            </div>
            {m.type === "image" && m.attachment_id && <img src={`${http.defaults.baseURL}/files/${m.attachment_id}`} alt="" className="rounded-lg mb-1 max-h-52" />}
            <div className="text-sm text-[#0F172A] whitespace-pre-wrap">{m.text || (m.type === "image" ? "📷" : m.type === "audio" ? "🎤" : "📎")}</div>
            {m.transcription && <div className="text-xs italic text-slate-500 mt-1">🎤 {m.transcription}</div>}
            <div className="text-[10px] text-slate-400 mt-1 text-right">{new Date(m.timestamp).toLocaleString(i18n.language, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} {m.status ? `· ${m.status}` : ""}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-slate-200 bg-white">
        {conv.mode === "ai" && <div className="text-xs text-amber-600 mb-2 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {t("inbox.manual_hint")}</div>}
        <div className="flex gap-2">
          <Input data-testid="operator-input" placeholder={t("inbox.type_message")} value={text}
            disabled={conv.mode === "ai"} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} className="h-11" />
          <Button data-testid="operator-send-btn" onClick={send} disabled={busy || conv.mode === "ai"} className="h-11 bg-emerald-600 hover:bg-emerald-700"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}

function SidePanel({ conv }) {
  const nav = useNavigate();
  const { t } = useTranslation();
  const p = conv.project;
  if (!p) return <div className="p-4 text-sm text-slate-400">{t("inbox.no_project")}</div>;
  return (
    <div className="h-full overflow-y-auto scrollbar-thin bg-white border-l border-slate-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <StatusBadge status={p.status} />
        <Button size="sm" variant="outline" data-testid="inbox-open-project-btn" onClick={() => nav(`/pratiche/${p.id}`)}>{t("inbox.open_project")}</Button>
      </div>
      <div className="text-xs text-slate-600 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="inbox-project-facts">
        {p.comune && <div><span className="text-slate-400">{t("pratiche.comune")}:</span> {p.comune}</div>}
        {p.material_hint && <div><span className="text-slate-400">{t("pratiche.material")}:</span> {p.material_hint}</div>}
        {p.finish_hint && <div><span className="text-slate-400">{t("pratiche.finish")}:</span> {p.finish_hint}</div>}
        {p.color_hint && <div><span className="text-slate-400">{t("pratiche.color")}:</span> {p.color_hint}{p.color_sides === "both" ? ` (${t("pratiche.sides_both")})` : ""}</div>}
        {p.measurements_status && <div><span className="text-slate-400">{t("pratiche.measures")}:</span> {t(`pratiche.ms_${p.measurements_status}`)}</div>}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {t("inbox.ai_summary")}</div>
        <p className="text-sm text-slate-700">{p.ai_summary || t("inbox.awaiting_data")}</p>
      </div>
      {p.missing_information?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-red-500 font-semibold mb-1">{t("inbox.missing_count")} ({p.missing_information.length})</div>
          <ul className="text-sm text-slate-700 space-y-1">{p.missing_information.map((m, i) => <li key={i} className="flex gap-1.5"><span className="text-red-400">•</span> {m}</li>)}</ul>
        </div>
      )}
      {p.optional_information?.length > 0 && (
        <div data-testid="inbox-optional-info">
          <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">{t("pratiche.optional")}</div>
          <ul className="text-xs text-slate-500 space-y-0.5">{p.optional_information.map((m, i) => <li key={i}>· {m}</li>)}</ul>
        </div>
      )}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">{t("pratiche.windows")} ({(p.openings || []).reduce((a, o) => a + (o.quantity || 1), 0)})</div>
        {(p.openings || []).map((o, i) => (
          <div key={i} className="text-sm text-slate-700 border border-slate-100 rounded-lg p-2 mb-1">
            <b>{o.type}</b> {o.room && `· ${o.room}`} {o.quantity > 1 && `· x${o.quantity}`}
            <div className="text-xs text-slate-500">{o.width ? `${o.width}×${o.height} ${o.unit}` : t("inbox.measures_todo")} {o.material && `· ${o.material}`} {(o.color_inside || o.color_outside) && `· Int: ${o.color_inside || "—"} · Est: ${o.color_outside || "—"}`}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Inbox() {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const { convId } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();

  const loadList = () => http.get("/conversations", { params: { q } }).then((r) => setConvs(r.data)).catch((e) => toast.error(apiErr(e)));
  const loadActive = (id) => http.get(`/conversations/${id}`).then((r) => setActive(r.data)).catch((e) => toast.error(apiErr(e)));

  useEffect(() => { loadList(); const tm = setInterval(loadList, 8000); return () => clearInterval(tm); }, [q]);
  useEffect(() => { if (convId) loadActive(convId); }, [convId]);
  useEffect(() => { if (active) { const tm = setInterval(() => loadActive(active.id), 6000); return () => clearInterval(tm); } }, [active?.id]);

  const select = (c) => { setActive(null); nav(`/inbox/${c.id}`); };

  if (!convs) return <Loading />;

  return (
    <div className="h-[calc(100vh-3.5rem)] flex" data-testid="inbox-page">
      <div className={`w-full lg:w-[320px] shrink-0 ${active ? "hidden lg:flex" : "flex"} flex-col`}>
        <ConvList convs={convs} activeId={active?.id} onSelect={select} q={q} setQ={setQ} />
      </div>
      {active ? (
        <>
          <div className="flex-1 min-w-0 flex flex-col">
            <button className="lg:hidden flex items-center gap-1 text-sm text-slate-600 p-2 bg-white border-b" onClick={() => { setActive(null); nav("/inbox"); }}><ArrowLeft className="w-4 h-4" /> {t("inbox.conversations")}</button>
            <div className="flex-1 min-h-0"><ChatThread conv={active} reload={() => loadActive(active.id)} /></div>
          </div>
          <div className="hidden xl:block w-[340px] shrink-0"><SidePanel conv={active} /></div>
        </>
      ) : (
        <div className="hidden lg:flex flex-1 items-center justify-center text-slate-400">{t("inbox.select_conv")}</div>
      )}
    </div>
  );
}
